import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ENDPOINT } from "constants/variables";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import SendIcon from "@mui/icons-material/Send";
import Textarea from "@mui/joy/Textarea";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import { helpHttp } from "service/HelpHttp";
import { setLoading } from "reducers/loadingSlice";
import { LoadingNews } from "components/utils/LoadingNews";
import { successErrorAction, warningErrorAction } from "actions/ErrorActions";
import ReportPurchaseSendEmail from "pages/purchaseDelivery/purchaseDeliverySendEmail/ReportPurchaseSendEmail";
import "css/generalSettings.css";
import "css/customDialog.css";

const initialStatePurchaseSendEmail = {
  id: "",
  defaultStore_id: 0,
  tradename: "",
  useraccount_id: 0,
  username: "",
  defaultformatnumber: 1,
  isfinished: false,
  purchasenote: 0,
  numberticket: 0,
  ownnumberpurchase: "",
  datepurchase: null,
  vehiclestore_id: 0,
  vehiclestore_licenseplate: "",
  vehiclestore_defaultdriver: "",
  vehiclestore_store_id: 0,
  rel_trans_store_id: 0,
  rel_trans_documnettype_id: 0,
  rel_trans_documentid: "",
  rel_trans_documnettype_name: "",
  rel_trans_tradename: "",
  rel_trans_name: "",
  rel_trans_contacperson: "",
  rel_trans_nima: "",
  rel_trans_pygr: "",
  rel_trans_grnp: "",
  rel_trans_opt: 0,
  rel_trans_roadtype_id: 0,
  rel_trans_street: "",
  rel_trans_country_id: 1,
  rel_trans_province_id: 30,
  rel_trans_postalcode_id: 0,
  ori_tras_store_id: 0,
  ori_tras_documnettype_id: 0,
  ori_tras_documnettype_name: "",
  ori_tras_documentid: "",
  ori_tras_tradename: "",
  ori_tras_name: "",
  ori_tras_contacperson: "",
  ori_tras_nima: "",
  ori_tras_pygr: "",
  ori_tras_grnp: "",
  ori_tras_opt: 0,
  ori_tras_workplace: "",
  ori_tras_roadtype_id: 0,
  ori_tras_street: "",
  ori_tras_country_id: 1,
  ori_tras_province_id: 30,
  ori_tras_postalcode_id: 0,
  ori_tras_email: "",
  observations: "",
  totalpurchasedelivery: 0,
  totaldiscountpurchasedelivery: 0,
  totaltaxespurchasedelivery: 0,
  totalNetoPurchasedelivery: 0,
  enabled: true,
  subownnumberpurchase: 0,
  payment_id: 0,
  payment_code: "",
  payment_namees: "",
  payment_nameen: "",
  totalWeigthNeto: 0,
  signature_dispatched_by: "",
  signature_client: "",
  linespurchasedelivery: [],
};

const initialStateEMAIL = {
  id: "",
  defaultStore_id: 0,
  useraccount_id: 0,
  store_EmailTo: "",
  subjectEmail: "",
  contentEmail: "",
  filetosend: [],
};

const initialEmailToSelect = [{ id: "", email: "" }];
const INVALID_CHARS_REGEX = /[\\!$%&|<>?{}~"']+/;
const CLEAN_SEARCH_REGEX = /[.;\-/\\|]/g;

/**
 * Debounced callback estable (no depende del render)
 */
function useDebouncedCallback(callback, delay = 300) {
  const cbRef = useRef(callback);
  const timeoutRef = useRef(null);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  return useCallback(
    (...args) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        cbRef.current?.(...args);
      }, delay);
    },
    [delay]
  );
}

const PurchaseDeliverySendEmail = ({ id, showModalPurchasesSendEmail, typeOfList }) => {
  const { t, i18n } = useTranslation(["listDataPurchases"]);
  const token = useSelector((state) => state.loginUser.token);
  const defaultStore = useSelector((state) => state.loginUser.defaultStore);
  const useraccount_id = useSelector((state) => state.loginUser.useraccount_id);
  const footeremail = useSelector((state) => state.loginUser.defaultticketsformat.footeremail);
  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formPurchaseSendEmail, setFormPurchaseSendEmail] = useState(initialStatePurchaseSendEmail);
  const [formEMAIL, setFormEMAIL] = useState(initialStateEMAIL);
  const [errorsEMAIL, setErrorsEMAIL] = useState({});
  const [pdfBlob, setPdfBlob] = useState(null);
  const [emailToSelect, setEmailToSelect] = useState(initialEmailToSelect);
  const [filteredEmail, setFilteredEmail] = useState([]);
  const [highlightedIndexEmail, setHighlightedIndexEmail] = useState(-1);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ---- PDF URL (sin estado extra innecesario)
  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return "";
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const PdfViewer = useMemo(() => {
    if (!pdfUrl) return null;
    return (
      <iframe
        src={pdfUrl}
        title="PDF Preview"
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    );
  }, [pdfUrl]);

  // ---- Rutas destino: memo para no recalcular
  const targetRoute = useMemo(() => {
    if (typeOfList === "1") return "/purchasedelivery";
    if (typeOfList === "2") return "/purchasedeliveryended";
    return "/purchasedeliverybegin";
  }, [typeOfList]);

  const resetStateAndClose = useCallback(() => {
    setFormPurchaseSendEmail(initialStatePurchaseSendEmail);
    setPdfBlob(null);
    setFormEMAIL(initialStateEMAIL);
    setErrorsEMAIL({});
    setEmailToSelect(initialEmailToSelect);
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
    dispatch(setLoading(false));
    navigate(targetRoute);
  }, [dispatch, navigate, targetRoute]);

  const handleCloseSendEmailPurchaseSignature = useCallback(() => {
    resetStateAndClose();
  }, [resetStateAndClose]);

  useEffect(() => {
    if (!showModalPurchasesSendEmail) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") resetStateAndClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showModalPurchasesSendEmail, resetStateAndClose]);

  const validateFormEmail = useCallback(
    (currentFormEmail = formEMAIL) => {
      const errs = {};
      if (!currentFormEmail.store_EmailTo) {
        errs.store_EmailTo = "Text_FieldSendEmailToRequired";
      } else if (INVALID_CHARS_REGEX.test(currentFormEmail.store_EmailTo)) {
        errs.store_EmailTo = "Text_TextNoValid";
      }
      if (INVALID_CHARS_REGEX.test(currentFormEmail.subjectEmail || "")) {
        errs.subjectEmail = "Text_TextNoValid";
      }
      if (INVALID_CHARS_REGEX.test(currentFormEmail.contentEmail || "")) {
        errs.contentEmail = "Text_TextNoValid";
      }
      return errs;
    },
    [formEMAIL]
  );

  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(String(reader.result || "").split(",")[1]);
      reader.onerror = reject;
    });
  }, []);

  const scrollToElement = useCallback((index) => {
    const dropdown = document.querySelector(".email-suggest-dropdown");
    const item = dropdown?.children?.[index];
    if (item) {
      item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  const runFilterEmail = useCallback(
    (searchValue) => {
      const q = (searchValue || "").toLowerCase().trim();
      if (!q) {
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
        return;
      }
      const filtered = emailToSelect
        .filter((s) => s?.email)
        .filter((s) => s.email.toLowerCase().includes(q))
        .slice(0, 20);

      setFilteredEmail(filtered);
      setHighlightedIndexEmail(filtered.length ? 0 : -1);
    },
    [emailToSelect]
  );

  const debouncedHandleSearchEmail = useDebouncedCallback(runFilterEmail, 300);
  const handleSelectEmail = useCallback((emailObj) => {
    if (!emailObj) return;
    setFormEMAIL((prev) => ({ ...prev, store_EmailTo: emailObj.email || "" }));
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
  }, []);

  const handleKeyDownEmail = useCallback(
    (event) => {
      if (event.key === "ArrowDown") {
        if (filteredEmail.length === 0 && (formEMAIL.store_EmailTo || "").trim() === "") {
          setFilteredEmail(emailToSelect);
          setHighlightedIndexEmail(0);
          return;
        }
        setHighlightedIndexEmail((prevIndex) => {
          const nextIndex = prevIndex < filteredEmail.length - 1 ? prevIndex + 1 : prevIndex;
          scrollToElement(nextIndex);
          return nextIndex;
        });
        return;
      }
      if (event.key === "ArrowUp") {
        setHighlightedIndexEmail((prevIndex) => {
          const prev = prevIndex > 0 ? prevIndex - 1 : prevIndex;
          scrollToElement(prev);
          return prev;
        });
        return;
      }
      if ((event.key === "Enter" || event.key === "Tab") && highlightedIndexEmail >= 0) {
        if (highlightedIndexEmail < filteredEmail.length) {
          event.preventDefault?.();
          handleSelectEmail(filteredEmail[highlightedIndexEmail]);
        }
        return;
      }
      if (event.key === "Escape") {
        const input = (formEMAIL.store_EmailTo || "").toLowerCase();
        const isValid = emailToSelect.some((e) => (e?.email || "").toLowerCase() === input);
        if (!isValid) {
          setFormEMAIL((prev) => ({ ...prev, store_EmailTo: "" }));
        }
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
      }
    },
    [
      emailToSelect,
      filteredEmail,
      formEMAIL.store_EmailTo,
      highlightedIndexEmail,
      handleSelectEmail,
      scrollToElement,
    ]
  );

  const handleBlur = useCallback((event) => {
    if (!event?.currentTarget) return;
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFilteredEmail([]);
      setHighlightedIndexEmail(-1);
    }
  }, []);

  const handleChangeDataEditEmail = useCallback((e) => {
    const { name, value } = e.target;
    setFormEMAIL((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDataEditEmail = useCallback(
    (e) => {
      handleChangeDataEditEmail(e);
      // validar tras el blur usando el estado resultante (aprox: validamos el actual)
      setErrorsEMAIL(validateFormEmail());
    },
    [handleChangeDataEditEmail, validateFormEmail]
  );

  // ---- Submit Email
  const handleSubmitSendEmail = useCallback(async () => {
    const errors = validateFormEmail(formEMAIL);
    setErrorsEMAIL(errors);
    const hasNoErrors = Object.keys(errors).length === 0;
    const hasEmailTo = !!formEMAIL?.store_EmailTo;
    const hasPdf = !!pdfBlob;
    if (!hasNoErrors || !hasEmailTo || !hasPdf) {
      if (errors.store_EmailTo) dispatch(warningErrorAction(t(errors.store_EmailTo)));
      else if (errors.subjectEmail) dispatch(warningErrorAction(t(errors.subjectEmail)));
      else if (errors.contentEmail) dispatch(warningErrorAction(t(errors.contentEmail)));
      else if (!hasPdf) dispatch(warningErrorAction(t("Text_FieldSendFileRequired")));
      else dispatch(warningErrorAction(t("Text_IncorrectData")));
      return;
    }
    dispatch(setLoading(true));
    try {
      const base64File = await blobToBase64(pdfBlob);
      const formData = {
        id,
        defaultStore_id: defaultStore.id,
        useraccount_id,
        store_EmailTo: formEMAIL.store_EmailTo,
        subjectEmail: formEMAIL.subjectEmail,
        contentEmail: formEMAIL.contentEmail,
        filetosend: base64File,
        fileName: `Alb_${formPurchaseSendEmail.purchasenote.toString().padStart(8, "0")}.pdf`,
        fileType: "application/pdf",
      };
      const getData = await helpHttp().postemail(
        ENDPOINT,
        "api/sendemailwithattachment",
        token,
        formData
      );
      dispatch(setLoading(false));
      if (getData?.status === "OK") dispatch(successErrorAction(t("Text_EMAIL_Sended")));
      else dispatch(warningErrorAction(t(getData?.cause || "Text_IncorrectData")));
      handleCloseSendEmailPurchaseSignature();
    } catch (e) {
      dispatch(setLoading(false));
      dispatch(warningErrorAction(t("Text_IncorrectData")));
      handleCloseSendEmailPurchaseSignature();
    }  
  }, [
    validateFormEmail,
    formEMAIL,
    pdfBlob,
    dispatch,
    t,
    blobToBase64,
    id,
    defaultStore.id,
    useraccount_id,
    token,
    formPurchaseSendEmail.purchasenote,
    handleCloseSendEmailPurchaseSignature,
  ]);

  // ---- Carga de sugerencias de email (cuando hay input y modal abierto)
  useEffect(() => {
    if (!showModalPurchasesSendEmail) return;
    const raw = formEMAIL.store_EmailTo || "";
    const search = raw ? raw.replace(CLEAN_SEARCH_REGEX, "").trim() : "-1";
    if (!search || search === "-1") {
      setEmailToSelect(initialEmailToSelect);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const getData = await helpHttp().get2(ENDPOINT, "api/getlistemails/", token, search);
        if (cancelled || !mountedRef.current) return;
        if (getData?.err) dispatch(warningErrorAction(t("Text_InvalidCredentials")));
        else setEmailToSelect(getData);
      } catch {
        if (!cancelled && mountedRef.current) dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModalPurchasesSendEmail, formEMAIL.store_EmailTo, token, dispatch, t]);
 
  useEffect(() => {
    if (!showModalPurchasesSendEmail) return;
    let cancelled = false;
    const load = async () => {
      dispatch(setLoading(true));
      try {
        if (id === "0") {
          if (cancelled || !mountedRef.current) return;
          setFormPurchaseSendEmail({ ...initialStatePurchaseSendEmail, linespurchasedelivery: [] });
          setFormEMAIL((prev) => ({
            ...prev,
            defaultStore_id: defaultStore.id,
            useraccount_id,
            contentEmail: footeremail,
          }));
          dispatch(setLoading(false));
          return;
        }
        const getData = await helpHttp().get2(
          ENDPOINT,
          "purchasesignature/getpurchasesignaturebyid/",
          token,
          id
        );
        if (cancelled || !mountedRef.current) return;
        if (getData?.err) {
          setFormPurchaseSendEmail(initialStatePurchaseSendEmail);
          dispatch(
            warningErrorAction(
              t(
                getData.message === "Load failed"
                  ? "Text_ConnectionFailied"
                  : "Text_InvalidCredentials"
              )
            )
          );
          dispatch(setLoading(false));
          return;
        }
        setFormPurchaseSendEmail(getData);
        setFormEMAIL((prev) => ({
          ...prev,
          defaultStore_id: defaultStore.id,
          useraccount_id,
          store_EmailTo: getData.ori_tras_email,
          subjectEmail: "LE REMITO EL ALBARÁN Nº: " + getData.purchasenote,
          contentEmail: footeremail,
        }));
      } catch {
        if (!cancelled && mountedRef.current) dispatch(warningErrorAction(t("Text_IncorrectData")));
      } finally {
        if (!cancelled && mountedRef.current) dispatch(setLoading(false));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showModalPurchasesSendEmail, dispatch, id, t, token, defaultStore.id, useraccount_id, footeremail]);

  const emailToWidth = useMemo(() => (i18n.language === "es" ? 738 : 867), [i18n.language]);
  const subjectWidth = useMemo(() => (i18n.language === "es" ? 941 : 937), [i18n.language]);

  const onChangeEmailTo = useCallback(
    (e) => {
      const next = (e.target.value || "").toLowerCase();
      setFormEMAIL((prev) => ({ ...prev, store_EmailTo: next }));
      debouncedHandleSearchEmail(next);
    },
    [debouncedHandleSearchEmail]
  );
  const showDropdown = filteredEmail.length > 0;
  const shouldMountReport = useMemo(() => {
    return (
      showModalPurchasesSendEmail &&
      !!formPurchaseSendEmail.id &&
      Array.isArray(formPurchaseSendEmail.linespurchasedelivery) &&
      formPurchaseSendEmail.linespurchasedelivery.length > 0
    );
  }, [showModalPurchasesSendEmail, formPurchaseSendEmail.id, formPurchaseSendEmail.linespurchasedelivery]);

  if (!showModalPurchasesSendEmail) return null;

  return (
    <>
      <div className="container-fluid email-send-wrapper">
        <Box sx={{ bgcolor: "#ffffff", width: "1050px" }}>
          <form onSubmit={handleCloseSendEmailPurchaseSignature}>
            {/* HEADER */}
            <div className="email-card-header">
              <div className="email-header-content">
                <div className="email-header-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="email-header-text">
                  <div className="email-header-title">{t("Text_NumberPurchase_EMAIL")}:</div>
                  <div className="email-header-number">{formPurchaseSendEmail.purchasenote}</div>
                </div>
                <div className="email-card-actions">
                  {!isLoadingData && (
                    <>
                      <button type="button" className="email-send-btn" onClick={handleSubmitSendEmail}>
                        {t("Button_SEND")}
                        <SendIcon sx={{ fontSize: 22, color: "white" }} />
                      </button>
                      <button
                        type="button"
                        className="email-cancel-btn"
                        onClick={handleCloseSendEmailPurchaseSignature}
                      >
                        {t("Button_Cancel")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            {!isLoadingData ? (
              <>
                {/* FORM EMAIL */}
                <div className="email-card-form">
                  <div className="row g-3">
                    {/* Email To */}
                    <div className="col-12 email-input" style={{ position: "relative" }}>
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t("Text_SENDEMAIL_EmailsTo")} :
                      </label>
                      <TextField
                        id="store_EmailTo"
                        name="store_EmailTo"
                        value={formEMAIL.store_EmailTo}
                        onChange={onChangeEmailTo}
                        onKeyDown={handleKeyDownEmail}
                        onBlur={(event) => {
                          if (!event.relatedTarget || !event.relatedTarget.closest(".email-suggest-dropdown")) {
                            handleBlur(event);
                          }
                        }}
                        label={
                          errorsEMAIL.store_EmailTo != null
                            ? t(errorsEMAIL.store_EmailTo)
                            : t("PlaceHolder_SENDEMAIL_EmailsTo")
                        }
                        placeholder={t("PlaceHolder_SENDEMAIL_EmailsTo")}
                        variant="standard"
                        inputProps={{
                          style: { width: emailToWidth },
                          maxLength: 100,
                        }}
                        InputLabelProps={{
                          style: { fontSize: 18, background: "#FFFFFF" },
                        }}
                      />
                      {showDropdown && (
                        <div className="email-suggest-dropdown" onMouseDown={(e) => e.preventDefault()}>
                          {filteredEmail.map((store, index) => {
                            const isActive = index === highlightedIndexEmail;
                            return (
                              <MenuItem
                                key={store.id}
                                onClick={() => handleSelectEmail(store)}
                                style={{
                                  cursor: "pointer",
                                  padding: "10px 12px",
                                  borderBottom: "1px solid #eee",
                                  fontSize: 18,
                                  backgroundColor: isActive ? "#4e73df" : "transparent",
                                  color: isActive ? "white" : "black",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                {store.email}
                              </MenuItem>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* Subject */}
                    <div className="col-12 email-input">
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t("Text_SENDEMAIL_Subject")}
                      </label>
                      <TextField
                        id="subjectEmail"
                        name="subjectEmail"
                        value={formEMAIL.subjectEmail ?? ""}
                        inputProps={{
                          style: { width: subjectWidth },
                          maxLength: 100,
                        }}
                        InputLabelProps={{
                          style: { fontSize: 18, background: "#FFFFFF" },
                        }}
                        label={
                          errorsEMAIL.subjectEmail != null
                            ? t(errorsEMAIL.subjectEmail)
                            : t("PlaceHolder_SENDEMAIL_Subject")
                        }
                        placeholder={t("PlaceHolder_SENDEMAIL_Subject")}
                        variant="standard"
                        onBlur={handleBlurDataEditEmail}
                        onChange={handleChangeDataEditEmail}
                      />
                    </div>
                    {/* Content */}
                    <div className="col-12">
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t("Text_SENDEMAIL_Content")} :
                      </label>
                      <Textarea
                        id="contentEmail"
                        name="contentEmail"
                        value={formEMAIL.contentEmail}
                        className="email-textarea"
                        placeholder={t("PlaceHolder_SENDEMAIL_Content")}
                        sx={{ width: 1000, fontSize: 18 }}
                        variant="standard"
                        onBlur={handleBlurDataEditEmail}
                        onChange={handleChangeDataEditEmail}
                        maxRows={8}
                      />
                    </div>
                  </div>
                </div>
                {/* PDF CARD */}
                <div className="email-card-pdf">{PdfViewer}</div>
              </>
            ) : (
              <LoadingNews />
            )}
          </form>
        </Box>
        {/* ReportPurchaseSendEmail: solo cuando hace falta */}
        {shouldMountReport && (
          <ReportPurchaseSendEmail formPurchaseSendEmail={formPurchaseSendEmail} setPdfBlob={setPdfBlob} />
        )}
        <br />
      </div>
    </>
  );
};

PurchaseDeliverySendEmail.propTypes = {
  id: PropTypes.string.isRequired,
  showModalPurchasesSendEmail: PropTypes.bool.isRequired,
  typeOfList: PropTypes.string.isRequired,
};

export default PurchaseDeliverySendEmail;